import { useState, useMemo } from "react";
import { Bell, Moon, Plus, Trash2, Mail, Clock, Globe } from "lucide-react";
import { Modal } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input, Select } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { usePreferences } from "@/hooks/usePreferences";
import { notifySaved } from "@/lib/notifySaved";
import {
  DAYS_OF_WEEK,
  NOTIFICATION_CATEGORIES,
  NOTIFICATION_CHANNELS,
  defaultNotificationSettings,
  validateQuietSchedule,
  type DayOfWeek,
  type NotificationCategory,
  type NotificationChannel,
  type QuietHoursSchedule,
  type UserNotificationSettings,
} from "@/lib/notifications";
import {
  COMMON_TIMEZONES,
  getDefaultTimezone,
  formatTimeDisplay,
  formatTimeRangeDisplay,
  type TimeFormat,
} from "@/lib/time";
import { cn } from "@/lib/utils";

export function NotificationSettingsDialog({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  const { prefs, setPref } = usePreferences();
  const [tab, setTab] = useState<"channels" | "quiet_hours" | "digest" | "time_zone">("channels");

  const currentTimezone = (prefs?.timezone as string) || getDefaultTimezone();
  const timeFormat: TimeFormat = (prefs?.time_format as TimeFormat) || "12h";
  const hour12 = timeFormat !== "24h";

  const settings: UserNotificationSettings = useMemo(() => {
    const raw = prefs?.notifications as Partial<UserNotificationSettings> | undefined;
    const defaults = defaultNotificationSettings(currentTimezone);
    if (!raw) return defaults;
    return {
      timezone: (prefs?.timezone as string) || raw.timezone || defaults.timezone,
      quiet_hours: Array.isArray(raw.quiet_hours) ? raw.quiet_hours : defaults.quiet_hours,
      channels: raw.channels || defaults.channels,
      digest: raw.digest || defaults.digest,
      web_push_enabled: Boolean(raw.web_push_enabled),
    };
  }, [prefs?.notifications, prefs?.timezone, currentTimezone]);

  const updateSettings = (next: UserNotificationSettings) => {
    setPref("notifications", next);
    notifySaved("Notification settings saved");
  };

  const handleTimezoneChange = (tz: string) => {
    setPref("timezone", tz);
    updateSettings({ ...settings, timezone: tz });
    notifySaved("Timezone preference saved");
  };

  const handleTimeFormatChange = (fmt: TimeFormat) => {
    setPref("time_format", fmt);
    notifySaved(`Time format set to ${fmt === "12h" ? "12-hour (AM/PM)" : "24-hour"}`);
  };

  // Channel matrix toggling
  const toggleChannel = (cat: NotificationCategory, ch: NotificationChannel) => {
    const curCat = settings.channels[cat] || defaultNotificationSettings().channels[cat];
    const updated = {
      ...settings,
      channels: {
        ...settings.channels,
        [cat]: {
          ...curCat,
          [ch]: !curCat[ch],
        },
      },
    };
    updateSettings(updated);
  };

  // Quiet hours schedule management
  const [newScheduleOpen, setNewScheduleOpen] = useState(false);
  const [schedForm, setSchedForm] = useState<Partial<QuietHoursSchedule>>({
    name: "",
    days: ["mon", "tue", "wed", "thu", "fri"],
    start_time: "19:00",
    end_time: "08:30",
    allow_urgent_override: true,
    action: "queue_for_digest",
  });
  const [schedError, setSchedError] = useState<string | null>(null);

  const toggleScheduleEnabled = (id: string) => {
    const next = settings.quiet_hours.map((s) => (s.id === id ? { ...s, enabled: !s.enabled } : s));
    updateSettings({ ...settings, quiet_hours: next });
  };

  const deleteSchedule = (id: string) => {
    const next = settings.quiet_hours.filter((s) => s.id !== id);
    updateSettings({ ...settings, quiet_hours: next });
  };

  const saveNewSchedule = () => {
    const val = validateQuietSchedule(schedForm);
    if (!val.valid) {
      setSchedError(val.error || "Invalid schedule");
      return;
    }
    const newSched: QuietHoursSchedule = {
      id: `sched_${Date.now()}`,
      name: schedForm.name!.trim(),
      enabled: true,
      days: schedForm.days || ["mon", "tue", "wed", "thu", "fri"],
      start_time: schedForm.start_time || "18:30",
      end_time: schedForm.end_time || "08:30",
      timezone: settings.timezone,
      allow_urgent_override: Boolean(schedForm.allow_urgent_override),
      action: schedForm.action || "queue_for_digest",
    };
    updateSettings({
      ...settings,
      quiet_hours: [...settings.quiet_hours, newSched],
    });
    setNewScheduleOpen(false);
    setSchedForm({
      name: "",
      days: ["mon", "tue", "wed", "thu", "fri"],
      start_time: "19:00",
      end_time: "08:30",
      allow_urgent_override: true,
      action: "queue_for_digest",
    });
    setSchedError(null);
  };

  const toggleDayInForm = (day: DayOfWeek) => {
    const currentDays = schedForm.days || [];
    const nextDays = currentDays.includes(day)
      ? currentDays.filter((d) => d !== day)
      : [...currentDays, day];
    setSchedForm({ ...schedForm, days: nextDays });
  };

  return (
    <Modal open={open} onClose={onClose} title="Notification Settings" wide>
      {/* Tabs */}
      <div className="flex border-b border-border mb-4 gap-4 text-xs font-medium overflow-x-auto">
        <button
          type="button"
          onClick={() => setTab("channels")}
          className={cn(
            "pb-2 border-b-2 -mb-px flex items-center gap-1.5 cursor-pointer whitespace-nowrap",
            tab === "channels" ? "border-primary text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <Bell size={14} aria-hidden="true" /> Channels &amp; Events
        </button>
        <button
          type="button"
          onClick={() => setTab("quiet_hours")}
          className={cn(
            "pb-2 border-b-2 -mb-px flex items-center gap-1.5 cursor-pointer whitespace-nowrap",
            tab === "quiet_hours" ? "border-primary text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <Moon size={14} aria-hidden="true" /> Quiet Hours ({settings.quiet_hours.filter((s) => s.enabled).length})
        </button>
        <button
          type="button"
          onClick={() => setTab("digest")}
          className={cn(
            "pb-2 border-b-2 -mb-px flex items-center gap-1.5 cursor-pointer whitespace-nowrap",
            tab === "digest" ? "border-primary text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <Mail size={14} aria-hidden="true" /> Daily Digest
        </button>
        <button
          type="button"
          onClick={() => setTab("time_zone")}
          className={cn(
            "pb-2 border-b-2 -mb-px flex items-center gap-1.5 cursor-pointer whitespace-nowrap",
            tab === "time_zone" ? "border-primary text-foreground font-semibold" : "border-transparent text-muted-foreground hover:text-foreground"
          )}
        >
          <Globe size={14} aria-hidden="true" /> Time &amp; Localization
        </button>
      </div>

      {/* Tab: Channels Matrix */}
      {tab === "channels" && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Configure how and where you receive updates for different activities in Cordite.
          </p>

          <div className="rounded-lg border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead className="bg-secondary/60 text-muted-foreground border-b border-border">
                  <tr>
                    <th className="p-2.5 font-semibold">Activity</th>
                    {NOTIFICATION_CHANNELS.map((ch) => (
                      <th key={ch.id} className="p-2.5 font-semibold text-center whitespace-nowrap">
                        {ch.short}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {NOTIFICATION_CATEGORIES.map((cat) => {
                    const chMap = settings.channels[cat.id] || defaultNotificationSettings().channels[cat.id];
                    return (
                      <tr key={cat.id} className="hover:bg-muted/30">
                        <td className="p-2.5">
                          <div className="font-medium text-foreground">{cat.label}</div>
                          <div className="text-[11px] text-muted-foreground">{cat.description}</div>
                        </td>
                        {NOTIFICATION_CHANNELS.map((ch) => (
                          <td key={ch.id} className="p-2.5 text-center">
                            <input
                              type="checkbox"
                              checked={Boolean(chMap?.[ch.id])}
                              onChange={() => toggleChannel(cat.id, ch.id)}
                              aria-label={`${cat.label} via ${ch.label}`}
                              className="rounded border-border cursor-pointer h-4 w-4"
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Tab: Quiet Hours */}
      {tab === "quiet_hours" && (
        <div className="space-y-4">
          <div className="flex items-center justify-between gap-2">
            <p className="text-xs text-muted-foreground">
              Define time windows when non-urgent notifications are held or muted.
            </p>
            <Button size="sm" variant="ghost" onClick={() => setNewScheduleOpen(true)} className="gap-1">
              <Plus size={13} aria-hidden="true" /> Add schedule
            </Button>
          </div>

          {/* List of schedules */}
          <div className="space-y-2">
            {settings.quiet_hours.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
                No quiet hours schedules configured. Notifications will deliver around the clock.
              </div>
            ) : (
              settings.quiet_hours.map((sched) => (
                <div
                  key={sched.id}
                  className={cn(
                    "flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 rounded-lg border",
                    sched.enabled ? "border-border bg-card" : "border-border/50 bg-muted/20 opacity-70"
                  )}
                >
                  <div className="space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-foreground">{sched.name}</span>
                      <Badge variant={sched.enabled ? "green" : "gray"}>
                        {sched.enabled ? "Active" : "Disabled"}
                      </Badge>
                      {sched.allow_urgent_override && (
                        <Badge variant="gray" className="text-[10px]">
                          Urgent bypass allowed
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-2 text-[11px] text-muted-foreground flex-wrap">
                      <Clock size={12} aria-hidden="true" />
                      <span>
                        {formatTimeRangeDisplay(sched.start_time, sched.end_time, hour12)} ({sched.timezone || settings.timezone})
                      </span>
                      <span>•</span>
                      <span>{sched.days.map((d) => d.toUpperCase()).join(", ")}</span>
                      <span>•</span>
                      <span>{sched.action === "queue_for_digest" ? "Queued for digest" : "Muted"}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 self-end sm:self-center">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => toggleScheduleEnabled(sched.id)}
                      className="text-xs h-7"
                    >
                      {sched.enabled ? "Disable" : "Enable"}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => deleteSchedule(sched.id)}
                      className="text-destructive h-7 w-7 p-0"
                      aria-label={`Delete ${sched.name}`}
                    >
                      <Trash2 size={13} aria-hidden="true" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Add schedule modal / sub-card */}
          {newScheduleOpen && (
            <div className="p-3.5 rounded-lg border border-primary/40 bg-card/90 space-y-3 mt-4">
              <div className="text-xs font-semibold">New Quiet Hours Schedule</div>
              {schedError && <div className="text-xs text-destructive">{schedError}</div>}

              <div className="space-y-2">
                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">Schedule Name</label>
                  <Input
                    placeholder="e.g. Afternoon Focus Time"
                    value={schedForm.name || ""}
                    onChange={(e) => setSchedForm({ ...schedForm, name: e.target.value })}
                    className="h-8 text-xs"
                  />
                </div>

                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">Active Days</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {DAYS_OF_WEEK.map((d) => {
                      const selected = (schedForm.days || []).includes(d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => toggleDayInForm(d.id)}
                          className={cn(
                            "px-2.5 py-1 rounded text-xs font-medium cursor-pointer border",
                            selected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-secondary text-muted-foreground border-border hover:bg-muted"
                          )}
                        >
                          {d.short}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground block mb-1">Start Time (24h)</label>
                    <Input
                      type="time"
                      value={schedForm.start_time || "18:30"}
                      onChange={(e) => setSchedForm({ ...schedForm, start_time: e.target.value })}
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground block mb-1">End Time (24h)</label>
                    <Input
                      type="time"
                      value={schedForm.end_time || "08:30"}
                      onChange={(e) => setSchedForm({ ...schedForm, end_time: e.target.value })}
                      className="h-8 text-xs"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="urgent_override"
                    checked={Boolean(schedForm.allow_urgent_override)}
                    onChange={(e) => setSchedForm({ ...schedForm, allow_urgent_override: e.target.checked })}
                    className="rounded border-border cursor-pointer h-4 w-4"
                  />
                  <label htmlFor="urgent_override" className="text-xs text-foreground cursor-pointer">
                    Allow urgent / critical alerts (P0, security) to bypass quiet hours
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button size="sm" variant="ghost" onClick={() => setNewScheduleOpen(false)}>
                  Cancel
                </Button>
                <Button size="sm" onClick={saveNewSchedule}>
                  Save Schedule
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tab: Digest & Delivery */}
      {tab === "digest" && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Configure when and how your daily summary briefing is compiled and delivered.
          </p>

          <div className="p-3.5 rounded-lg border border-border bg-card space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-semibold">Morning Briefing Digest</div>
                <div className="text-[11px] text-muted-foreground">
                  Summarizes tasks due today, blocked items, and queued notifications.
                </div>
              </div>
              <input
                type="checkbox"
                checked={settings.digest.enabled}
                onChange={(e) =>
                  updateSettings({
                    ...settings,
                    digest: { ...settings.digest, enabled: e.target.checked },
                  })
                }
                className="rounded border-border cursor-pointer h-4 w-4"
              />
            </div>

            {settings.digest.enabled && (
              <div className="pt-2 border-t border-border space-y-3">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground block mb-1">
                      Delivery Time ({formatTimeDisplay(settings.digest.delivery_time, hour12)})
                    </label>
                    <Input
                      type="time"
                      value={settings.digest.delivery_time}
                      onChange={(e) =>
                        updateSettings({
                          ...settings,
                          digest: { ...settings.digest, delivery_time: e.target.value },
                        })
                      }
                      className="h-8 text-xs"
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-medium text-muted-foreground block mb-1">Delivery Channel</label>
                    <Select
                      value={settings.digest.channel}
                      onChange={(e) =>
                        updateSettings({
                          ...settings,
                          digest: { ...settings.digest, channel: e.target.value as "email" | "slack_dm" },
                        })
                      }
                      className="h-8 w-full text-xs"
                    >
                      <option value="email">Email</option>
                      <option value="slack_dm">Slack Direct Message</option>
                    </Select>
                  </div>
                </div>

                <div>
                  <label className="text-[11px] font-medium text-muted-foreground block mb-1">Delivery Days</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {DAYS_OF_WEEK.map((d) => {
                      const selected = settings.digest.days.includes(d.id);
                      return (
                        <button
                          key={d.id}
                          type="button"
                          onClick={() => {
                            const nextDays = selected
                              ? settings.digest.days.filter((x) => x !== d.id)
                              : [...settings.digest.days, d.id];
                            updateSettings({
                              ...settings,
                              digest: { ...settings.digest, days: nextDays },
                            });
                          }}
                          className={cn(
                            "px-2.5 py-1 rounded text-xs font-medium cursor-pointer border",
                            selected
                              ? "bg-primary text-primary-foreground border-primary"
                              : "bg-secondary text-muted-foreground border-border hover:bg-muted"
                          )}
                        >
                          {d.short}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Tab: Time & Localization */}
      {tab === "time_zone" && (
        <div className="space-y-4">
          <p className="text-xs text-muted-foreground">
            Configure your preferred timezone and hour display. Dates, activity timestamps, meetings, and quiet hours will be formatted in this zone.
          </p>

          <div className="space-y-4">
            {/* Hour Format Section */}
            <div className="p-3.5 rounded-lg border border-border bg-card space-y-3">
              <div>
                <div className="text-xs font-semibold text-foreground">Time Format (AM/PM vs 24h)</div>
                <div className="text-[11px] text-muted-foreground">
                  Hours are displayed in 12-hour AM/PM format by default. You can switch to 24-hour military format at any time.
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                <label
                  className={cn(
                    "flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors",
                    hour12 ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
                  )}
                >
                  <input
                    type="radio"
                    name="time_format"
                    value="12h"
                    checked={hour12}
                    onChange={() => handleTimeFormatChange("12h")}
                    className="cursor-pointer"
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-foreground">12-Hour (AM / PM) · Default</div>
                    <div className="text-[11px] text-muted-foreground">e.g. 9:00 AM, 2:30 PM, 11:45 PM</div>
                  </div>
                </label>

                <label
                  className={cn(
                    "flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors",
                    !hour12 ? "border-primary bg-primary/5" : "border-border hover:bg-muted/30"
                  )}
                >
                  <input
                    type="radio"
                    name="time_format"
                    value="24h"
                    checked={!hour12}
                    onChange={() => handleTimeFormatChange("24h")}
                    className="cursor-pointer"
                  />
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-foreground">24-Hour (Military)</div>
                    <div className="text-[11px] text-muted-foreground">e.g. 09:00, 14:30, 23:45</div>
                  </div>
                </label>
              </div>
            </div>

            {/* Timezone Section */}
            <div className="p-3.5 rounded-lg border border-border bg-card space-y-3">
              <div>
                <div className="text-xs font-semibold text-foreground">Your Timezone</div>
                <div className="text-[11px] text-muted-foreground">
                  Current detected browser timezone: <span className="font-medium text-foreground">{getDefaultTimezone()}</span>
                </div>
              </div>

              <div className="space-y-2">
                <label className="text-[11px] font-medium text-muted-foreground block">
                  Select Timezone
                </label>
                <Select
                  value={currentTimezone}
                  onChange={(e) => handleTimezoneChange(e.target.value)}
                  className="w-full text-xs"
                >
                  {COMMON_TIMEZONES.map((tz) => (
                    <option key={tz.value} value={tz.value}>
                      {tz.label} ({tz.value})
                    </option>
                  ))}
                  {/* Keep custom selected timezone if not in COMMON_TIMEZONES list */}
                  {!COMMON_TIMEZONES.some((tz) => tz.value === currentTimezone) && (
                    <option value={currentTimezone}>{currentTimezone}</option>
                  )}
                </Select>

                <div className="flex items-center justify-between text-[11px] text-muted-foreground pt-1">
                  <span>Current time in selected timezone:</span>
                  <span className="font-mono text-foreground font-medium">
                    {new Intl.DateTimeFormat("en-US", {
                      timeZone: currentTimezone,
                      hour: "numeric",
                      minute: "2-digit",
                      second: "2-digit",
                      hour12,
                      timeZoneName: "short",
                    }).format(new Date())}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
